import { useNavigate } from "react-router-dom";
import { ChevronDown, Copy, FolderOpen, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useProjectStore } from "./useProjectStore";

/**
 * Header dropdown listing locally-saved drafts. Shown in the AppShell
 * once ``projects.length > 0`` so first-time users get the cleanest
 * possible landing.
 */
export function ProjectsMenu() {
  const navigate = useNavigate();
  const projects = useProjectStore((s) => s.projects);
  const activeId = useProjectStore((s) => s.activeId);
  const setActive = useProjectStore((s) => s.setActive);
  const remove = useProjectStore((s) => s.remove);
  const newProject = useProjectStore((s) => s.newProject);
  const duplicateProject = useProjectStore((s) => s.duplicateProject);

  if (!projects.length) return null;
  const active = projects.find((p) => p.id === activeId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="text-white/70 hover:bg-white/10 hover:text-white">
          <FolderOpen className="h-3.5 w-3.5" />
          <span className="hidden md:inline">{active?.name ?? "Moji projekti"}</span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[280px]">
        {projects.map((p) => (
          <div key={p.id} className="flex items-center">
            <DropdownMenuItem
              className="flex-1"
              onSelect={() => {
                setActive(p.id);
                navigate(`/project/${p.id}/${stepPath(p.currentStep)}`);
              }}
            >
              <div className="flex flex-1 flex-col gap-0.5">
                <span className="font-medium">{p.name}</span>
                <span className="text-[0.7rem] text-stone-400">
                  {stepLabel(p.currentStep)} ·{" "}
                  {new Date(p.updatedAt).toLocaleDateString("hr-HR")}
                </span>
              </div>
            </DropdownMenuItem>
            <button
              type="button"
              className="rounded p-1 text-stone-400 hover:bg-ind-50 hover:text-ind-700"
              title="Kreni od ovog projekta (kopija) — promijeni samo datum i detalje"
              onClick={(e) => {
                e.stopPropagation();
                const id = duplicateProject(p.id);
                navigate(`/project/${id}/provjeri`);
              }}
            >
              <Copy className="h-3 w-3" />
            </button>
            <button
              type="button"
              className="mr-1 rounded p-1 text-stone-400 hover:bg-red-50 hover:text-red-700"
              title="Obriši draft"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Obrisati "${p.name}"?`)) remove(p.id);
              }}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            const id = newProject();
            navigate(`/project/${id}/donesi`);
          }}
        >
          <Plus className="h-4 w-4" /> Novi projekt
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Draft step → route segment in the Donesi → Provjeri → Preuzmi flow. */
function stepPath(step: 1 | 2 | 3 | undefined): string {
  return step === 3 ? "preuzmi" : step === 2 ? "provjeri" : "donesi";
}

/** Draft step → short label for the project list. */
function stepLabel(step: 1 | 2 | 3 | undefined): string {
  return step === 3 ? "Dokumenti" : step === 2 ? "Provjeri" : "Donesi";
}
